<template>
  <div class="m-articles">
    <van-nav-bar
      :title="title"
      left-arrow
      @click-left="$router.back()"
      fixed
      placeholder
    />
    <!-- 骨架屏加载 -->
    <div v-if="loading" class="m-articles__skeleton">
      <div v-for="n in 4" :key="n" class="m-articles__skeleton-card">
        <div class="m-articles__skeleton-header">
          <div class="m-articles__skeleton-title"></div>
          <div class="m-articles__skeleton-tag"></div>
        </div>
        <div class="m-articles__skeleton-line"></div>
        <div class="m-articles__skeleton-line m-articles__skeleton-line--short"></div>
        <div class="m-articles__skeleton-footer">
          <div class="m-articles__skeleton-chip"></div>
          <div class="m-articles__skeleton-date"></div>
        </div>
      </div>
    </div>

    <div class="m-articles__list" v-else-if="articleList.length > 0">
      <div
        v-for="(item, index) in articleList"
        :key="index"
        class="m-articles__card"
      >
        <div class="m-articles__card-header">
          <span
            class="m-articles__card-title"
            :class="{ 'm-articles__card-title--clickable': canClickTitle }"
            @click="canClickTitle && goDetail(item)"
          >{{ item.title }}</span>
          <van-tag
            v-if="statusText"
            :type="statusTagType"
            size="medium"
          >{{ statusText }}</van-tag>
        </div>
        <div class="m-articles__card-summary">{{ item.summary }}</div>
        <div class="m-articles__card-info">
          <span v-if="item.categoryName" class="m-articles__card-tag" :style="{ background: item.categoryColor || '#23B4DC' }">{{ item.categoryName }}</span>
          <span class="m-articles__card-source">来源：{{ item.source || item.author || '-' }}</span>
        </div>
        <div class="m-articles__card-stats" v-if="tab === 'published'">
          <span>点赞 {{ item.likeCount || 0 }}</span>
          <span>收藏 {{ item.favoriteCount || 0 }}</span>
          <span>评论 {{ item.commentCount || 0 }}</span>
        </div>
        <div class="m-articles__card-date">{{ item.date }}</div>

        <!-- 留言文章 -->
        <div v-if="tab === 'commented'" class="m-articles__extra">
          <div class="m-articles__extra-row">
            <span class="m-articles__extra-label">我的评论：</span>
            <span class="m-articles__extra-value">{{ item.myContent || '-' }}</span>
          </div>
          <div class="m-articles__extra-row">
            <span class="m-articles__extra-label">评论时间：</span>
            <span class="m-articles__extra-value">{{ item.updateTime || '-' }}</span>
          </div>
          <van-button plain type="danger" size="small" class="m-articles__action" @click="handleDeleteComment(item)">删除评论</van-button>
        </div>

        <!-- 收藏文章 -->
        <div v-if="tab === 'favorite'" class="m-articles__extra">
          <div class="m-articles__extra-row">
            <span class="m-articles__extra-label">收藏时间：</span>
            <span class="m-articles__extra-value">{{ item.updateTime || '-' }}</span>
          </div>
          <van-button plain type="danger" size="small" class="m-articles__action" @click="handleCancelFavorite(item)">取消收藏</van-button>
        </div>

        <!-- 点赞文章 -->
        <div v-if="tab === 'like'" class="m-articles__extra">
          <div class="m-articles__extra-row">
            <span class="m-articles__extra-label">点赞时间：</span>
            <span class="m-articles__extra-value">{{ item.updateTime || '-' }}</span>
          </div>
          <van-button plain type="danger" size="small" class="m-articles__action" @click="handleCancelLike(item)">取消点赞</van-button>
        </div>

        <!-- 驳回文章 -->
        <div v-if="tab === 'rejected'" class="m-articles__reject">
          <div class="m-articles__reject-reason">驳回原因：{{ item.approvalReason || '-' }}</div>
          <div class="m-articles__reject-time">驳回时间：{{ item.approvalTime || '-' }}</div>
          <van-button type="primary" size="small" class="m-articles__action" @click="handleEdit(item)">编辑</van-button>
        </div>
      </div>
    </div>

    <van-empty v-else-if="!loading" description="暂无相关文章" />
  </div>
</template>

<script>
import mobileViewport from './mixins/viewport'
import { Toast, Dialog } from 'vant'
import {
  getCompanyNewsList,
  getMemberNewsList,
  getMemberCommentList,
  getMemberFavoriteList,
  getMemberLikeList,
  deleteMemberComment,
  toggleFavorite,
  toggleLike
} from '../../api/news'

export default {
  mixins: [mobileViewport],
  name: 'MobileArticles',
  props: {
    tab: {
      type: String,
      default: 'pending'
    }
  },
  data() {
    return {
      userType: localStorage.getItem('userType') || 'personal',
      articleList: [],
      pageNum: 1,
      pageSize: 10,
      loading: false
    }
  },
  computed: {
    title() {
      const map = {
        pending: '审核中的文章',
        rejected: '已驳回的文章',
        published: '已发布的文章',
        commented: '我评论的文章',
        favorite: '我收藏的文章',
        like: '我点赞的文章'
      }
      return map[this.tab] || '我的文章'
    },
    queryStatus() {
      const map = { pending: 1, rejected: 2, published: 3 }
      return map[this.tab] || 1
    },
    statusText() {
      const map = { pending: '审核中', rejected: '已驳回', published: '已发布' }
      return map[this.tab] || ''
    },
    statusTagType() {
      const map = { pending: 'warning', rejected: 'danger', published: 'success' }
      return map[this.tab] || 'default'
    },
    canClickTitle() {
      return ['published', 'commented', 'favorite', 'like'].includes(this.tab)
    }
  },
  watch: {
    tab() {
      this.getArticleList()
    }
  },
  mounted() {
    this.getArticleList()
  },
  methods: {
    async getArticleList() {
      const apiType = this.getApiType()
      if (!apiType) return
      this.loading = true
      try {
        let list = []
        if (['pending', 'rejected', 'published'].includes(this.tab)) {
          list = await this.getMyNewsList()
        } else {
          list = await this.getMemberList(apiType)
        }
        this.articleList = list
      } catch (err) {
        Toast(err.message || '获取文章列表失败')
      } finally {
        this.loading = false
      }
    },
    getApiType() {
      const map = {
        pending: 'news',
        rejected: 'news',
        published: 'news',
        commented: 'comment',
        favorite: 'favorite',
        like: 'like'
      }
      return map[this.tab] || null
    },
    async getMyNewsList() {
      const api = this.userType === 'company' ? getCompanyNewsList : getMemberNewsList
      const res = await api({ queryStatus: this.queryStatus, pageNum: this.pageNum, pageSize: this.pageSize })
      const result = res.data || {}
      const list = result.records || []
      return list.map(item => ({
        id: item.id,
        newsId: item.id,
        newsCode: item.newsCode || '',
        title: item.title || '',
        summary: item.summary || '',
        author: item.author || '',
        source: item.source || '',
        categoryName: item.categoryName || '',
        categoryColor: item.categoryColor || '',
        likeCount: item.likeCount || 0,
        favoriteCount: item.favoriteCount || 0,
        commentCount: item.commentCount || 0,
        approvalReason: item.approvalReason || '',
        approvalTime: item.approvalTime || '',
        date: this.tab === 'published' ? (item.publishTime || '') : (item.createTime || '')
      }))
    },
    async getMemberList(apiType) {
      const apiMap = {
        comment: getMemberCommentList,
        favorite: getMemberFavoriteList,
        like: getMemberLikeList
      }
      const res = await apiMap[apiType]({ pageNum: this.pageNum, pageSize: this.pageSize })
      const result = res.data || {}
      const list = result.records || []
      return list.map(item => ({
        id: item.id,
        newsId: item.newsId,
        newsCode: item.newsCode || '',
        title: item.title || '',
        summary: item.summary || '',
        source: item.source || '',
        author: item.source || '',
        coverImage: item.coverImage || '',
        likeCount: item.likeCount || 0,
        favoriteCount: item.favoriteCount || 0,
        commentCount: item.commentCount || 0,
        myContent: item.content || '',
        updateTime: item.updateTime || '',
        date: item.publishTime || ''
      }))
    },
    handleEdit(item) {
      this.$router.push({ name: 'mobile-publish', query: { id: item.id } })
    },
    goDetail(item) {
      const newsCode = item.newsCode
      if (!newsCode) return
      this.$router.push(`/detail?newsCode=${newsCode}`)
    },
    async handleDeleteComment(item) {
      if (!item.id) return
      Dialog.confirm({
        title: '提示',
        message: '确定要删除这条留言吗？'
      }).then(() => {
        // 用户点击确认
        this.doDeleteComment(item)
      }).catch(() => {
        // 用户点击取消
      })
    },
    async doDeleteComment(item) {
      if (!item.id) return
      try {
        await deleteMemberComment({ id: item.id })
        Toast('删除成功')
        this.getArticleList()
      } catch (err) {
        Toast(err.message || '删除失败')
      }
    },
    async handleCancelFavorite(item) {
      if (!item.newsId) return
      Dialog.confirm({
        title: '提示',
        message: '确定要取消收藏吗？'
      }).then(() => {
        // 用户点击确认
        this.doCancelFavorite(item)
      }).catch(() => {
        // 用户点击取消
      })
    },
    async doCancelFavorite(item) {
      if (!item.newsId) return
      try {
        await toggleFavorite({ newsId: item.newsId, status: 0 })
        Toast('取消收藏成功')
        this.getArticleList()
      } catch (err) {
        Toast(err.message || '取消收藏失败')
      }
    },
    async handleCancelLike(item) {
      if (!item.newsId) return
      Dialog.confirm({
        title: '提示',
        message: '确定要取消点赞吗？'
      }).then(() => {
        // 用户点击确认
        this.doCancelLike(item)
      }).catch(() => {
        // 用户点击取消
      })
    },
    async doCancelLike(item) {
      if (!item.newsId) return
      try {
        await toggleLike({ newsId: item.newsId, status: 0 })
        Toast('取消点赞成功')
        this.getArticleList()
      } catch (err) {
        Toast(err.message || '取消点赞失败')
      }
    }
  }
}
</script>

<style scoped>
.m-articles {
  min-height: 100vh;
  background: #F5F7FA;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

/* ====== 骨架屏 ====== */
.m-articles__skeleton {
  padding: 12px;
}

.m-articles__skeleton-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.m-articles__skeleton-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}

.m-articles__skeleton-title {
  flex: 1;
  height: 18px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

.m-articles__skeleton-tag {
  width: 56px;
  height: 20px;
  flex-shrink: 0;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

.m-articles__skeleton-line {
  height: 14px;
  border-radius: 4px;
  margin-bottom: 8px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

.m-articles__skeleton-line--short {
  width: 65%;
}

.m-articles__skeleton-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 14px;
}

.m-articles__skeleton-chip {
  width: 72px;
  height: 16px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

.m-articles__skeleton-date {
  width: 48px;
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

/* ====== 列表 ====== */
.m-articles__list {
  padding: 12px;
}

.m-articles__card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.m-articles__card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.m-articles__card-title {
  flex: 1;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  line-height: 1.4;
}

.m-articles__card-title--clickable {
  cursor: pointer;
}

.m-articles__card-title--clickable:active {
  opacity: 0.7;
}

.m-articles__card-summary {
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 10px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.m-articles__card-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}

.m-articles__card-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  color: #fff;
}

.m-articles__card-source {
  font-size: 12px;
  color: #999;
}

.m-articles__card-stats {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
}

.m-articles__card-date {
  font-size: 12px;
  color: #bbb;
}

.m-articles__extra {
  margin-top: 8px;
  padding: 10px;
  background: #F5F7FA;
  border-radius: 8px;
}

.m-articles__extra-row {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.m-articles__extra-label {
  color: #999;
}

.m-articles__action {
  margin-top: 8px;
}

.m-articles__reject {
  margin-top: 8px;
  padding: 10px;
  background: #FFF1F0;
  border-radius: 8px;
  font-size: 12px;
  color: #FF4D4F;
}

.m-articles__reject-reason,
.m-articles__reject-time {
  margin-bottom: 4px;
}

.m-articles__reject .m-articles__action {
  background: #0091EA;
  border: none;
  color: #fff;
}
</style>
